import React, { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import RestUtilities from '../../services/RestUtilities';
import {
  uploadsState,
  artistState,
  loadingState,
} from '../../state/RecoilState';
import { uploadType } from '../../Constants';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';

import { Artist, Upload } from '../../@types/api';
import FileCard from '../FileCard';
import MfsDropzone from './MfsDropzone';

interface Props {
  isModal: boolean;
  handleUploadSelect?: (upload: Upload) => void;
  fileTypeId: number;
  handleClose?: () => void;
}
const FileManager: React.FC<Props> = ({
  isModal,
  fileTypeId,
  handleUploadSelect,
  handleClose,
}) => {
  const setLoading = useSetRecoilState(loadingState);
  const [uploads, setUploads] = useRecoilState<Upload[] | undefined>(
    uploadsState
  );
  const artist = useRecoilValue<Artist | undefined>(artistState);

  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [altTextNew, setAltTextNew] = useState<string>('');
  const [video, setVideo] = useState<string>('');
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const isVideo = [
    uploadType.video,
    uploadType.liveLoungeVideo,
    uploadType.youtube,
    uploadType.liveReelVideo,
  ].includes(fileTypeId);

  const isImage = [
    uploadType.image,
    uploadType.poster,
    uploadType.blog,
  ].includes(fileTypeId);

  const isVideoValid = video.length <= 100 && video.length > 0;
  const altTextNewIsValid = altTextNew.length <= 50 && altTextNew.length > 0;
  const altTextLabel = isImage
    ? 'Add the Alt Text'
    : fileTypeId === uploadType.youtube
    ? 'Add the YouTube video name'
    : 'Add the Song name';

  const items = uploads
    ?.filter((x: Upload) => x.fileTypeId === fileTypeId)
    .sort((a: Upload, b: Upload) => {
      return b.uploadId - a.uploadId;
    });

  useEffect(() => {
    const fetchUploads = async (artistId: number) => {
      setLoading(true);
      const url = `api/Upload/${artistId}`;
      const response = await RestUtilities.get(url);
      setLoading(false);
      if (response.ok) {
        setUploads(response.content);
        setLoading(false);
      }
    };

    if (artist && !uploads) {
      const artistId = artist.artistId;
      fetchUploads(artistId);
    }
  }, [artist, setLoading, setUploads, uploads]);

  const handleAltTextNewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const altTextNew = e.target.value;
    setAltTextNew(altTextNew);
  };

  const handleBlobUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = e.target.value;
    setVideo(video);
  };

  const onUseSelected = (upl: Upload | undefined) => {
    if (handleUploadSelect && upl) {
      handleUploadSelect(upl);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    const filePreviews = acceptedFiles.map((file: File) =>
      URL.createObjectURL(file)
    );
    setFiles(acceptedFiles);
    setFilePreviews(filePreviews);
  };

  const onItemSelect = (upload: Upload) => {
    setSelectedId(upload.uploadId);
  };

  const onUpdate = async (updateItem: Upload) => {
    const url = `api/Upload/${updateItem.uploadId}/${artist?.artistId}`;
    await RestUtilities.put(url, updateItem);
    const updated = uploads?.filter((x) => x.uploadId !== updateItem.uploadId);
    updated?.push(updateItem);
    setUploads(updated);
  };

  const onUpload = () => {
    const isBlobImage = fileTypeId === uploadType.blog;
    const actionType = fileTypes.find((x) => x.id === fileTypeId);
    if (!actionType) return;
    setLoading(true);
    const url = isBlobImage
      ? `/api/Upload/${actionType.apiAction}`
      : `/api/Upload/${actionType.apiAction}/${artist?.artistId}`;
    if (files.length > 0) {
      files.forEach(async (file: File) => {
        const formData = new FormData();
        formData.append('File', file);
        formData.append('AltText', altTextNew);
        const response = await RestUtilities.postFormData(url, formData);
        if (response.ok) {
          if (isBlobImage) {
            onUseSelected(response.content);
          } else {
            const newUploads: Upload[] = uploads ? [...uploads] : [];
            newUploads.push(response.content);
            setUploads(newUploads);
            onUseSelected(response.content);
          }
        }
      });
    }
    setLoading(false);
  };

  const resetFiles = () => {
    setFiles([]);
    setFilePreviews([]);
    setAltTextNew('');
  };

  const getCurrentFileType = () => {
    const currentfileType = fileTypes.find((x) => x.show);
    return currentfileType?.id;
  };

  const postVideo = async () => {
    setLoading(true);
    if (fileTypeId === uploadType.youtube) {
      const url = `/api/Upload/YouTubeVideo/${artist?.artistId}`;
      const data = {
        url: video,
        videoName: altTextNew,
      };
      const response = await RestUtilities.post(url, data);
      if (response.ok) {
        handleUploadSelect && handleUploadSelect(response.content);
      } else {
        handleClose && handleClose();
      }
    } else {
      const url = `/api/Upload/Video/${artist?.artistId}`;
      let fileType = fileTypeId ? fileTypeId : getCurrentFileType();
      const data = {
        blobUrl: video,
        songName: altTextNew,
        isLiveLounge: fileType === uploadType.liveLoungeVideo,
      };
      const response = await RestUtilities.post(url, data);
      if (response.ok) {
        handleUploadSelect && handleUploadSelect(response.content);
      } else {
        handleClose && handleClose();
      }
    }
    setLoading(false);
  };

  const renderVideoDetailsCapture = () => {
    return (
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-start'
        mb={3}
      >
        <TextField
          type='text'
          value={video}
          label={
            fileTypeId === uploadType.youtube ? 'YouTube ref' : 'Blob File Name'
          }
          placeholder={
            fileTypeId === uploadType.youtube
              ? 'e.g. 6uaP8yG-N1E'
              : 'A blob filename like JETS_REEL_1.mp4'
          }
          size='small'
          onChange={handleBlobUrlChange}
          error={!(video.length > 0 && isVideoValid)}
          sx={{ mr: 1, minWidth: 250 }}
        />
        <TextField
          id='formBasicText'
          label={altTextLabel}
          type='text'
          value={altTextNew}
          placeholder='Enter text'
          onChange={handleAltTextNewChange}
          error={!altTextNewIsValid}
          disabled={!(video.length > 0 && isVideoValid)}
          size='small'
          sx={{ mr: 1, minWidth: 250 }}
        />
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={postVideo}
        >
          Add {altTextNew}
        </Button>
      </Box>
    );
  };

  const renderAddNewFile = () => {
    return (
      <>
        <Grid item sm={12} className='new-file'>
          {isVideo ? (
            renderVideoDetailsCapture()
          ) : (
            <Box display='flex' alignItems='center' justifyContent='flex-start'>
              <MfsDropzone
                onDrop={onDrop}
                files={files}
                fileTypeId={fileTypeId}
                filePreviews={filePreviews}
              />
              {fileTypeId !== uploadType.blog && (
                <TextField
                  fullWidth
                  id='formBasicText'
                  label={altTextLabel}
                  type='text'
                  value={altTextNew}
                  placeholder='Enter text'
                  onChange={handleAltTextNewChange}
                  error={!altTextNewIsValid}
                  disabled={files.length === 0}
                  size='small'
                  sx={{ maxWidth: '200px', mx: 1 }}
                />
              )}
              <Button
                variant='contained'
                color='secondary'
                onClick={resetFiles}
                size='small'
                sx={{ mx: 1 }}
                disabled={files.length === 0}
              >
                Clear
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={onUpload}
                size='small'
                sx={{ mx: 1 }}
                disabled={
                  !(
                    files.length > 0 &&
                    (altTextNewIsValid || fileTypeId === uploadType.blog)
                  )
                }
              >
                Use {altTextNewIsValid ? `'${altTextNew}'` : 'a new upload'}
              </Button>
            </Box>
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      {isModal && (
        <>
          <Box>{renderAddNewFile()}</Box>

          {fileTypeId !== uploadType.blog && (
            <Box
              my={2}
              display='flex'
              justifyContent='flex-start'
              alignItems='center'
            >
              <Typography variant='body1'>
                Or select an existing file from below
              </Typography>
            </Box>
          )}
        </>
      )}

      <Grid container spacing={2}>
        {items?.map((x: Upload) => {
          return (
            <Grid
              key={x.uploadId}
              item
              lg={3}
              md={4}
              sm={6}
              onClick={(e) => onItemSelect(x)}
            >
              <FileCard
                upload={x}
                isSelected={selectedId === x.uploadId}
                onUpdate={onUpdate}
                isModal={isModal}
                onItemSelect={onItemSelect}
                onUseSelected={onUseSelected}
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default FileManager;

const fileTypes = [
  {
    id: uploadType.image,
    displayName: 'Images',
    show: true,
    apiAction: 'Image',
    renderType: uploadType.image,
    adminOnly: false,
  },
  {
    id: uploadType.audio,
    displayName: 'Mp3s',
    show: false,
    apiAction: 'Audio',
    renderType: uploadType.audio,
    adminOnly: false,
  },
  {
    id: uploadType.video,
    displayName: 'Videos',
    show: false,
    apiAction: 'Video',
    renderType: uploadType.video,
    adminOnly: true,
  },
  {
    id: uploadType.poster,
    displayName: 'Video Posters',
    show: false,
    apiAction: 'VideoPoster',
    renderType: uploadType.poster,
    adminOnly: true,
  },
  {
    id: uploadType.liveLoungeVideo,
    displayName: 'Live Lounge Videos',
    show: false,
    apiAction: 'Video',
    renderType: uploadType.video,
    adminOnly: false,
  },
  {
    id: uploadType.youtube,
    displayName: 'YouTube Videos',
    show: false,
    apiAction: 'YouTubeVideo',
    renderType: uploadType.youtube,
    adminOnly: false,
  },
  {
    id: uploadType.blog,
    displayName: 'Blog Image',
    show: false,
    apiAction: 'BlogImage',
    renderType: uploadType.image,
    adminOnly: true,
  },
];
